import { useState } from "react";
import { toast } from "sonner";
import { Camera, HardHat, WarningCircle, CheckCircle, User, MapPin } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { capturePhotoWithGeo } from "@/lib/photoWatermark";

/**
 * Mobile-first "field" observation reporter for supervisors and workers.
 *
 * Route: /hse/field. Renders a large-tap, touch-friendly full-screen form to
 * quickly report an observation (kind, area, description, immediate action,
 * severity, optional photo with camera access). Optimized for narrow screens
 * (single column, big buttons, no dialog) — thumbs and gloves friendly.
 *
 * Cada foto pasa por `capturePhotoWithGeo` que además de compresión:
 *   1. Pide `navigator.geolocation` (permiso del usuario).
 *   2. Estampa una marca de agua con fecha, coordenadas y nombre.
 *   3. Devuelve `geo` que se envía al backend junto con la observación.
 */

const KINDS = [
  { v: "unsafe_condition", label: "Condición insegura", color: "bg-orange-500" },
  { v: "unsafe_act", label: "Acto inseguro", color: "bg-amber-500" },
  { v: "positive", label: "Observación positiva", color: "bg-emerald-600" },
];
const SEVS = [
  { v: "low", label: "Baja", color: "bg-emerald-500" },
  { v: "medium", label: "Media", color: "bg-amber-500" },
  { v: "high", label: "Alta", color: "bg-red-600" },
];

export default function HseFieldReport() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    kind: "unsafe_condition", area: "", description: "",
    immediate_action: "", severity: "medium", photo: null, status: "open",
    geo: null,
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // last sent id or "offline"

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Máx 8MB"); return; }
    try {
      const { dataUrl, geo } = await capturePhotoWithGeo(f, {
        reporter: user?.name || "sin sesión",
        area: form.area,
      });
      setForm((p) => ({ ...p, photo: dataUrl, geo: geo || p.geo }));
      if (geo) toast.success(`GPS capturado ±${Math.round(geo.accuracy_m)}m`);
      else toast.warning("Sin GPS · marca de agua sin coordenadas");
    } catch (err) {
      toast.error("No se pudo procesar la foto");
    }
  };

  const submit = async () => {
    if (!form.area.trim() || !form.description.trim()) {
      toast.error("Área y descripción son obligatorias");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/hse/observations", form);
      const isOffline = Boolean(res?.data?._offline);
      setSent(isOffline ? "offline" : res.data.id);
      toast.success(isOffline ? "Guardada offline — se sincronizará" : "Observación enviada");
      setForm({
        kind: "unsafe_condition", area: "", description: "",
        immediate_action: "", severity: "medium", photo: null, status: "open",
        geo: null,
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      <div className="sticky top-0 z-10 bg-orange-600 text-white px-4 py-4 shadow" data-testid="field-header">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white/15 flex items-center justify-center">
            <HardHat size={22} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-black text-lg leading-none">Reporte de campo</p>
            <p className="text-[11px] opacity-90 truncate">
              <User size={11} className="inline mr-1" /> {user?.name || "Sin sesión"}
            </p>
          </div>
        </div>
      </div>

      {sent && (
        <div className="mx-4 mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="field-success-banner">
          <CheckCircle size={22} weight="fill" className="text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-heading font-bold text-emerald-800">¡Observación registrada!</p>
            <p className="text-xs text-emerald-700">
              {sent === "offline"
                ? "Guardada en la cola offline. Se enviará al recuperar la conexión."
                : "Gracias por reportar. Puedes registrar otra a continuación."}
            </p>
          </div>
        </div>
      )}

      <div className="px-4 pt-4 space-y-5">
        {/* Kind — big pills */}
        <div>
          <Label className="overline-label text-slate-600">Tipo</Label>
          <div className="grid grid-cols-1 gap-2 mt-1.5" data-testid="field-kind-group">
            {KINDS.map((k) => (
              <button
                key={k.v}
                type="button"
                data-testid={`field-kind-${k.v}`}
                onClick={() => setForm((p) => ({ ...p, kind: k.v }))}
                className={`flex items-center gap-3 rounded-lg border-2 p-4 text-left transition ${form.kind === k.v ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white"}`}
              >
                <span className={`h-3 w-3 rounded-full ${k.color}`} />
                <span className="text-base font-semibold text-slate-800">{k.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Area */}
        <div>
          <Label className="overline-label text-slate-600">Área / Nivel</Label>
          <Input
            data-testid="field-area"
            value={form.area}
            onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))}
            placeholder="Ej. Galería 850 este"
            className="mt-1.5 h-12 text-base"
            autoComplete="off"
          />
        </div>

        {/* Description */}
        <div>
          <Label className="overline-label text-slate-600">¿Qué observaste?</Label>
          <Textarea
            data-testid="field-description"
            rows={4}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Describe el hallazgo con el mayor detalle posible"
            className="mt-1.5 text-base"
          />
        </div>

        {/* Immediate action */}
        <div>
          <Label className="overline-label text-slate-600">Acción inmediata (opcional)</Label>
          <Textarea
            data-testid="field-action"
            rows={2}
            value={form.immediate_action}
            onChange={(e) => setForm((p) => ({ ...p, immediate_action: e.target.value }))}
            placeholder="Retiré personal, avisé al supervisor…"
            className="mt-1.5 text-base"
          />
        </div>

        {/* Severity — big pills */}
        <div>
          <Label className="overline-label text-slate-600">Severidad</Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="field-severity-group">
            {SEVS.map((s) => (
              <button
                key={s.v}
                type="button"
                data-testid={`field-sev-${s.v}`}
                onClick={() => setForm((p) => ({ ...p, severity: s.v }))}
                className={`rounded-lg border-2 p-3 font-bold text-white transition ${s.color} ${form.severity === s.v ? "ring-4 ring-blue-300 scale-[1.02]" : "opacity-60"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Photo */}
        <div>
          <Label className="overline-label text-slate-600">Foto (opcional)</Label>
          <label className="mt-1.5 flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-lg p-4 cursor-pointer bg-white active:bg-slate-100">
            <Camera size={24} weight="duotone" className="text-blue-900" />
            <span className="text-base text-slate-700 flex-1">
              {form.photo ? "Foto lista ✓ — toca para reemplazar" : "Tomar / subir foto"}
            </span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickPhoto} data-testid="field-photo-input" />
          </label>
          {form.photo && (
            <img alt="preview" src={form.photo} className="mt-2 rounded-lg max-h-56 w-full object-cover" data-testid="field-photo-preview" />
          )}
          {form.geo && (
            <div className="mt-2 flex items-center gap-2 rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-800" data-testid="field-geo-chip">
              <MapPin size={14} weight="fill" className="text-sky-600" />
              <span className="font-mono">
                {form.geo.lat.toFixed(6)}, {form.geo.lng.toFixed(6)}
                <span className="text-sky-500"> · ±{Math.round(form.geo.accuracy_m)}m</span>
              </span>
            </div>
          )}
        </div>

        {/* Submit — big fat button */}
        <Button
          data-testid="field-submit"
          onClick={submit}
          disabled={busy || !form.area.trim() || !form.description.trim()}
          className="w-full h-14 text-base font-bold bg-orange-600 hover:bg-orange-700 gap-2"
        >
          <WarningCircle size={22} weight="fill" />
          {busy ? "Enviando…" : "Enviar reporte"}
        </Button>

        <p className="text-[11px] text-slate-400 text-center pt-2">
          Sistema SGSSO · Registro instantáneo desde campo
        </p>
      </div>
    </div>
  );
}
